<template>
  <Modal
    :show="visible"
    :title="t('Upload Payment Receipt')"
    size="medium"
    :close-label="t('Cancelar')"
    :close-icon="'pi pi-times'"
    :save-label="t('Enviar')"
    :save-icon="'pi pi-upload'"
    :save-fn="triggerUpload"
    :save-disabled-fn="() => !selectedFile || isUploading"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <div class="receipt-upload-dialog" data-testid="payroll-receipt-upload-dialog">
      <!-- Execution Info -->
      <box v-if="execution" type="primary" class="p-mb-3">
        <div class="execution-info">
          <div class="info-row">
            <span class="label">{{ t('Colaborador') }}:</span>
            <span class="value">{{ execution.user_name }}</span>
          </div>
          <div class="info-row">
            <span class="label">{{ t('Payment Amount') }}:</span>
            <span class="value">{{ n(parseFloat(String(execution.amount)) || 0, 'currency') }}</span>
          </div>
          <div class="info-row" v-if="execution.payment_date">
            <span class="label">{{ t('Data de pagamento') }}:</span>
            <span class="value">{{ execution.payment_date }}</span>
          </div>
        </div>
      </box>

      <!-- Existing Receipt -->
      <box v-if="execution.receipt_url" type="primary" class="p-mb-3">
        <div class="card-title">
          <i class="pi pi-file-pdf"></i>
          <span>{{ t('Current Receipt') }}</span>
        </div>
        <div class="existing-receipt">
          <a :href="execution.receipt_url" target="_blank" class="receipt-link">
            <i class="pi pi-external-link"></i>
            {{ t('Ver comprovante atual') }}
          </a>
          <small class="p-text-secondary">
            {{ t('Enviar um novo comprovante substituirá o atual') }}
          </small>
        </div>
      </box>

      <!-- File Upload — no wrapper component available for file inputs -->
      <div class="file-upload-area p-mb-3">
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          data-testid="payroll-receipt-file-input"
          @change="onFileInputChange"
        />
        <small class="time-info">{{ t('Arraste e solte o arquivo aqui ou clique para selecionar') }}</small>
      </div>

      <!-- File Info -->
      <div v-if="selectedFile" class="file-info p-mb-3">
        <div class="file-details">
          <i class="pi pi-file"></i>
          <div class="file-meta">
            <span class="file-name">{{ selectedFile.name }}</span>
            <span class="file-size">{{ formatFileSize(selectedFile.size) }}</span>
          </div>
        </div>
      </div>

      <!-- Upload Progress -->
      <div v-if="isUploading" class="upload-progress p-mb-3">
        <progress-bar :value="uploadProgress" :show-value="true" />
        <small>{{ t('Uploading receipt to Azure Blob Storage...') }}</small>
      </div>

      <!-- Upload Instructions -->
      <inline-message severity="info" class="p-mb-3">
        {{ t('Formatos aceitos') }}: PDF, JPG, PNG ({{ t('máx') }} 5MB)
      </inline-message>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useReceipts } from '@/composables/useReceipts';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';

type Id = number | string;

interface Execution {
  id: Id;
  user_name?: string;
  amount?: number | string;
  payment_date?: string;
  receipt_url?: string;
  [key: string]: unknown;
}

interface UploadResult {
  receipt_url?: string;
  [key: string]: unknown;
}

const props = defineProps<{
  execution: Execution;
}>();

const emit = defineEmits<{
  close: [];
  uploaded: [result: UploadResult];
}>();

const { t, n } = useI18n();
const { uploadReceipt, uploadProgress, isUploading } = useReceipts();

const visible = ref(true);
const selectedFile = ref<File | null>(null);

const onFileInputChange = (event: Event): void => {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0] ?? null;
  selectedFile.value = file;
  if (file) {
    log('ReceiptUploadDialog', 'onFileInputChange', 'File selected', {
      name: file.name,
      size: file.size,
      type: file.type
    });
  }
};

const triggerUpload = (): void => {
  if (selectedFile.value) {
    handleUpload(selectedFile.value);
  }
};

const handleUpload = async (file: File): Promise<void> => {
  if (!file) {
    eventBus.emit('globalMessage.warning', {
      message: t('Por favor selecione um arquivo para enviar'),
      duration: 3000
    });
    return;
  }

  log('ReceiptUploadDialog', 'handleUpload', 'Starting upload', {
    executionId: props.execution.id,
    fileName: file.name,
    fileSize: file.size
  });

  try {
    const result = await uploadReceipt(props.execution.id, file) as UploadResult;

    eventBus.emit('receipt.uploaded', {
      executionId: props.execution.id,
      receiptUrl: result.receipt_url
    });

    eventBus.emit('globalMessage.success', {
      message: t('Comprovante enviado com sucesso'),
      duration: 3000
    });

    emit('uploaded', result);
    handleClose();
  } catch (err) {
    const error = err as Error;
    console.error('Error uploading receipt:', error);
    eventBus.emit('globalMessage.error', {
      message: error.message || t('Falha ao enviar comprovante'),
      duration: 5000
    });
  }
};

const handleClose = (): void => {
  visible.value = false;
  emit('close');
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};
</script>

<style scoped>
.receipt-upload-dialog {
  width: 100%;
}

.execution-info {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--surface-200);
}

.info-row:last-child {
  border-bottom: none;
}

.info-row .label {
  font-weight: 500;
  color: var(--text-color-secondary);
}

.info-row .value {
  font-weight: 600;
  color: var(--text-color);
}

.card-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.card-title i {
  color: var(--primary-color);
}

.existing-receipt {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.receipt-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 500;
}

.receipt-link:hover {
  text-decoration: underline;
}

.file-info {
  background-color: var(--surface-50);
  border: 1px solid var(--surface-200);
  border-radius: 6px;
  padding: 1rem;
}

.file-details {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.file-details i {
  font-size: 2rem;
  color: var(--primary-color);
}

.file-meta {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.file-name {
  font-weight: 500;
  color: var(--text-color);
}

.file-size {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
}

.upload-progress {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.upload-progress small {
  color: var(--text-color-secondary);
  text-align: center;
}
</style>
